import { domainProvider } from '@mathflat/shared/@common/domain/domainProvider'
import { subDays } from 'date-fns'
import i18next, { t } from 'i18next'
import { makeAutoObservable } from 'mobx'
import type { PaymentService } from 'src/service/Payment.service'

import type { Academy, Teacher, User } from '@/dto'
import type { B2gContract } from '@/dto/B2g/B2gContract'
import { UserPermission } from '@/models/(Permission)/UserPermission'
import { CustomRouterStore } from '@/router/CustomRouterStore'
import { envUtils } from '@/utils/envUtils'

class CommonRepo {
  teacherId?: Teacher['teacherId'] // 사용하지 않는 것으로 보이나 앱 등도 확인이 필요해서 일단 유지
  user?: User
  permission = new UserPermission({
    userAuthority: 'MANAGER_TEACHER',
    academyFeatureAuthority: 'BASIC',
    subscribedCurriculums: null,
    payStatus: '무료체험',
    isSchoolflat: false,
    isSeoltab: false,
    isFranchise: false,
  })
  academy?: Academy
  router = new CustomRouterStore()
  payment: PaymentService | null = null
  b2gContract: B2gContract | null = null
  version = '' // 안드로이드 등 디바이스 버전
  device = '' //  디바이스

  hasWorksheet = false

  constructor() {
    makeAutoObservable(this)
  }

  clear() {
    this.academy = this.teacherId = this.user = undefined
  }

  get isSolo() {
    return this.permission.bySubscription.isSolo
  }

  get isSchoolflat() {
    return (
      envUtils.isSchool ||
      !!(this.academy?.customerType && this.academy.customerType?.includes('B2G'))
    )
  }

  get isSeoltab() {
    return this.academy?.academyAdditionalData.isSeoltab ?? false
  }

  private get _studentContractPlans() {
    if (
      !this.isSchoolflat ||
      !this.academy ||
      !this.academy.isStudentContractPlan ||
      !this.academy.specialContractPlans
    )
      return null
    const { specialContractPlans } = this.academy
    return specialContractPlans.map((plan, index, plans) => {
      const nextPlan = plans[index + 1]
      return {
        ...plan,
        contractStartDate: new Date(plan.contractStartDate),
        contractEndDate: nextPlan
          ? subDays(new Date(nextPlan.contractStartDate), 1)
          : this.academy?.payEndDate
            ? new Date(this.academy.payEndDate)
            : null,
      }
    })
  }
  get currentStudentContractPlan() {
    return this._studentContractPlans?.[0]
  }
  get nextStudentContractPlans() {
    const plans = this._studentContractPlans?.slice(1)
    return plans?.length ? plans : null
  }
  // window.location.host랑 다른 값
  // ui상에서 사용되는 host (window.location.host가 mathflat.com이어도 ui상에서 schoolflat.com으로 보여야할 때 있음)
  get host() {
    if (this.isSchoolflat) {
      return `schoolflat.com`
    }
    return `${domainProvider.TARGET_DOMAIN}`
  }

  get appName() {
    if (this.isSchoolflat) {
      return 'schoolflat'
    }
    return 'mathflat'
  }

  get appKorName() {
    if (this.isSchoolflat) {
      return '스쿨플랫'
    }
    return '매쓰플랫'
  }

  get agencyNameInKor() {
    if (i18next.exists('학원')) {
      return t('학원')
    }
    // 언어팩 로드 실패 시 isSchoolflat 분기로 fallback
    return this.isSchoolflat ? '학교' : '학원'
  }

  get isFranchise() {
    return this.academy?.isFranchise ?? false
  }

  get isCreverse() {
    return this.academy?.isCreverse ?? false
  }

  get isVine() {
    return this.academy?.isVine ?? false
  }

  get isStudentManageReadOnly() {
    return !this.permission.byFeature.isAllowed('STUDENT_MANAGE_EDIT')
  }

  get isTeacherManageReadOnly() {
    return !this.permission.byFeature.isAllowed('TEACHER_MANAGE_EDIT')
  }

  get isClassManageReadOnly() {
    return !this.permission.byFeature.isAllowed('CLASS_MANAGE_EDIT')
  }

  /** 역할·브랜드 판단은 BE 가 하므로 FE 에 브랜드 분기를 두지 않는다. */
  get isAcademyInfoViewAllowed() {
    return this.permission.byFeature.isAllowed('ACADEMY_INFO_VIEW')
  }

  get isAcademyInfoReadOnly() {
    return !this.permission.byFeature.isAllowed('ACADEMY_INFO_EDIT')
  }

  /**
   * 나의DB 노출 여부. restriction 축이 우선이고, restriction 이 안 내려온 계정만
   * 기존 브랜드·결제 게이팅을 따른다. 판정 불가일 때는 숨긴다.
   */
  get isMyDbAllowed() {
    return (
      (this.permission?.byFeature?.isAllowed('MY_DB') ?? false) &&
      (this.permission?.get?.prepare?.학습지?.나의DB?.access ?? false)
    )
  }

  get isAllowedToUseDefaultLogo() {
    return this.permission.byFeature.isAllowed('ACADEMY_LOGO_UPDATE_DEFAULT')
  }

  get isAcademyLogoUpdateAllowed() {
    return this.permission.byFeature.isAllowed('ACADEMY_LOGO_UPDATE')
  }

  get isPasswordChangeAllowed() {
    return this.permission.byFeature.isAllowed('TEACHER_PASSWORD_UPDATE')
  }

  /** 교재비 정산을 쓸 수 있는지. 학부모 결제와 별도 축이라 각각 내려온다. */
  get isSettlementAllowed() {
    return this.permission.byFeature.isAllowed('SETTLEMENT')
  }

  /** 교재 주문에서 학부모 결제를 쓸 수 있는지. 막히면 결제 주체 탭에서 빠진다. */
  get isParentPaymentAllowed() {
    return this.permission.byFeature.isAllowed('ORDER_PARENT_PAYMENT')
  }

  get isElectronApp() {
    return window.bridge !== undefined
  }
}

const commonRepo = new CommonRepo()
export default commonRepo
